import React, { useEffect, useRef, useState } from 'react';
import { StorylineApi } from '../../API/StorylineApi';
import Button from '../../Components/Button';
import { AnalyticsEvent, useAnalytics } from '../../Context/AnalyticsProvider';
import { StorylineDilemmaOption } from '../../Types/StorylineDilemma';

interface StorylineDilemmaNewOptionModalProps {
  dilemmaId: string;
  onOptionCreated: (option: StorylineDilemmaOption) => void;
  onCancel: () => void;
}

const StorylineDilemmaNewOptionModal: React.FC<
  StorylineDilemmaNewOptionModalProps
> = ({ dilemmaId, onOptionCreated, onCancel }) => {
  const { trackEvent } = useAnalytics();
  const [newOptionTitle, setNewOptionTitle] = useState('');
  const newOptionTitleRef = useRef<string>();
  newOptionTitleRef.current = newOptionTitle;
  const [newOptionDescription, setNewOptionDescription] = useState('');
  const newOptionDescriptionRef = useRef<string>();
  newOptionDescriptionRef.current = newOptionDescription;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !isSubmitting) {
        e.preventDefault();
        handleCreateOption();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSubmitting]);

  const handleCreateOption = async () => {
    if (!newOptionTitleRef.current) {
      setError('Please fill in a title');
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      // Track dilemma choice creation requested
      await trackEvent(AnalyticsEvent.DILEMMA_CHOICE_CREATION_REQUESTED, {
        source: 'StorylineDilemmaNewOptionModal',
        dilemmaId: dilemmaId,
        title: newOptionTitleRef.current,
        description: newOptionDescriptionRef.current,
      });

      const storylineApi = new StorylineApi();
      const newOption = await storylineApi.createCustomDilemmaOption(
        dilemmaId,
        newOptionTitleRef.current,
        newOptionDescriptionRef.current,
      );

      // Track dilemma choice creation completed
      await trackEvent(AnalyticsEvent.DILEMMA_CHOICE_CREATION_COMPLETED, {
        source: 'StorylineDilemmaNewOptionModal',
        dilemmaId: dilemmaId,
        newOption: newOption,
      });

      onOptionCreated(newOption);
    } catch (err) {
      // Track dilemma choice creation failed
      await trackEvent(AnalyticsEvent.DILEMMA_CHOICE_CREATION_FAILED, {
        source: 'StorylineDilemmaNewOptionModal',
        dilemmaId: dilemmaId,
        error: err.response?.data?.error || err.message,
        title: newOptionTitleRef.current,
        description: newOptionDescriptionRef.current,
      });

      if (err.response?.status === 400) {
        setError(err.response.data.error);
      } else {
        setError('Failed to create an option. Please try again.');
      }
      console.error('Error creating custom option:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-lg font-semibold mb-4">Create Your Own Option</h3>
      <div className="space-y-4 text-left">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            autoFocus
            type="text"
            value={newOptionTitle}
            onChange={(e) => setNewOptionTitle(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            placeholder="Enter a title for your option"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={newOptionDescription}
            onChange={(e) => setNewOptionDescription(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            rows={3}
            placeholder="Describe your option"
          />
        </div>
        {error && <div className="text-red-500 text-sm">{error}</div>}
        <div className="flex gap-2 pt-2 justify-end">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateOption}
            disabled={isSubmitting}
            isInProgress={isSubmitting}
            inProgressText="Creating..."
          >
            Create
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StorylineDilemmaNewOptionModal;
